import React, { useState, useMemo } from 'react';
import { useInstrumentProfiles } from '@hooks/useInstrumentProfiles';
import ProfileCard from './ProfileCard';
import { Modal, Input, Button, Badge } from '../../../components/ui';
import { Search, Music, Filter } from 'lucide-react';

interface ProfileBrowserProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (profileKey: string) => void;
  selectedKey?: string | null;
  excludeKey?: string | null;
  contextLabel?: string;
}

const ProfileBrowser: React.FC<ProfileBrowserProps> = ({
  isOpen,
  onClose,
  onSelect,
  selectedKey,
  excludeKey,
  contextLabel = 'Select Profile',
}) => {
  const { data: instrumentProfiles } = useInstrumentProfiles();
  const [searchTerm, setSearchTerm] = useState('');
  const [genreFilter, setGenreFilter] = useState('');

  const allGenres = useMemo(() => {
    const set = new Set<string>();
    Object.values(instrumentProfiles).forEach(p => p.genres.forEach(g => set.add(g)));
    return Array.from(set).sort();
  }, [instrumentProfiles]);

  const profileEntries = useMemo(() => Object.entries(instrumentProfiles), [instrumentProfiles]);

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return profileEntries.filter(([, profile]) => {
      // Search filter
      const matchesSearch = !term || [
        profile.name,
        profile.description,
        profile.identity.era,
        profile.identity.movement,
        ...profile.genres,
      // User-created profiles can lack identity.era/movement (e.g. "Purple Neon
      // Circuitry") -- skip missing fields instead of crashing the whole tab.
      ].some(field => typeof field === 'string' && field.toLowerCase().includes(term));

      // Genre filter
      const matchesGenre = !genreFilter || profile.genres.includes(genreFilter);

      return matchesSearch && matchesGenre;
    });
  }, [profileEntries, searchTerm, genreFilter]);

  const totalCount = profileEntries.length;

  const handleSelect = (key: string) => {
    onSelect(key);
    onClose();
    setSearchTerm('');
    setGenreFilter('');
  };

  const handleClose = () => {
    onClose();
    setSearchTerm('');
    setGenreFilter('');
  };

  const headerContent = (
    <div className="flex items-center gap-3">
      <div className="p-2.5 bg-accent/20 rounded-xl text-accent">
        <Music size={20} />
      </div>
      <div>
        <span className="text-xl font-black text-slate-50 uppercase tracking-tighter">
          {contextLabel}
        </span>
        <p className="text-[11px] text-slate-400 font-bold uppercase tracking-widest mt-1">
          Browse instrument production profiles
        </p>
      </div>
    </div>
  );

  const footerContent = (
    <div className="flex justify-between items-center">
      <p className="text-[11px] text-slate-400 font-bold uppercase">
        Showing {filtered.length} of {totalCount} profiles
      </p>
      <Button variant="secondary" size="sm" onClick={handleClose}>
        Cancel
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title=""
      size="full"
      footer={footerContent}
      className="max-h-[85vh] flex flex-col rounded-3xl"
    >
      {/* Custom Header */}
      <div className="px-2 pb-5 border-b border-slate-800 -mt-2 mb-4">
        {headerContent}
      </div>

      {/* Search + Genre Filter */}
      <div className="pb-5 border-b border-slate-800 mb-4 space-y-3">
        <Input
          placeholder="Search by name, genre, era, description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          leftElement={<Search size={18} className="text-slate-400" />}
          autoFocus
          className="rounded-2xl py-3.5"
        />
        <div className="flex items-center gap-2 flex-wrap">
          <Filter size={14} className="text-slate-400" />
          <button
            onClick={() => setGenreFilter('')}
            className={`px-2.5 py-1 rounded-full text-[11px] font-black uppercase tracking-wider transition-all ${
              !genreFilter
                ? 'bg-accent/20 text-accent border border-accent/30'
                : 'bg-slate-800/50 text-slate-400 border border-slate-700/50 hover:text-slate-300'
            }`}
          >
            All
          </button>
          {allGenres.map(genre => (
            <button
              key={genre}
              onClick={() => setGenreFilter(genreFilter === genre ? '' : genre)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-black uppercase tracking-wider transition-all ${
                genreFilter === genre
                  ? 'bg-accent/20 text-accent border border-accent/30'
                  : 'bg-slate-800/50 text-slate-400 border border-slate-700/50 hover:text-slate-300'
              }`}
            >
              {genre}
            </button>
          ))}
        </div>
      </div>

      {/* Card Grid */}
      <div className="flex-grow overflow-y-auto custom-scrollbar -mx-2 px-2">
        {filtered.length === 0 ? (
          <div className="h-64 flex flex-col items-center justify-center text-slate-700">
            <Music size={48} className="opacity-20 mb-4" />
            <p className="font-black uppercase tracking-widest text-sm">No matching profiles</p>
            <p className="text-[11px] text-slate-400 mt-2">Try adjusting your search or genre filter</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map(([key, profile]) => (
              <ProfileCard
                key={key}
                profile={profile}
                variant="browser"
                isSelected={selectedKey === key}
                isExcluded={excludeKey === key}
                onClick={() => handleSelect(key)}
              />
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ProfileBrowser;
